import { Controller } from "@hotwired/stimulus"
import { v4 as uuidv4 } from "uuid"

// Connects to data-controller="answer-csv-upload"
export default class extends Controller {
  static targets = [
    "fileInput",
    "errorMessage",
    "successMessage"
  ]
  static values = {
    questionId: String
  }

  static MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
  static MESSAGE_TIMEOUT = 5000 // 5 sec
  static VALID_FILE_TYPES = [ "text/csv" ]
  static MESSAGE_TYPES = {
    SUCCESS: "success",
    ERROR: "error"
  }

  connect() {
    this.fileInputTarget.addEventListener("change", this.handleFileSelect.bind(this))
  }

  disconnect() {
    this.fileInputTarget.removeEventListener("change", this.handleFileSelect.bind(this))

    if (this.successTimeout) clearTimeout(this.successTimeout)
    if (this.errorTimeout) clearTimeout(this.errorTimeout)
  }

  triggerFileInput() {
    this.clearMessages()
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]

    if (!file) return

    if (!this.validateFile(file)) {
      this.fileInputTarget.value = ""
      return
    }

    this.uploadFile(file)
  }

  validateFile(file) {
    const fileExtension = file.name.split(".").pop().toLowerCase()

    if (!this.constructor.VALID_FILE_TYPES.includes(file.type) && fileExtension !== "csv") {
      this.displayError("Please select a CSV file")
      return false
    }

    if (file.size > this.constructor.MAX_FILE_SIZE) {
      this.displayError("File size must be less than 5MB")
      return false
    }

    return true
  }

  uploadFile(file) {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const csvContent = event.target.result
        const answers = this.parseCSV(csvContent)
        this.displaySuccess(`Successfully imported ${answers.length} answers`)
        this.dispatchAnswersUploadedEvent({
          importedCount: answers.length,
          answers
        })
      } catch (error) {
        this.displayError(error.message)
      } finally {
        this.fileInputTarget.value = ""
      }
    }

    reader.onerror = () => {
      this.displayError("Error reading file")
      this.fileInputTarget.value = ""
    }

    reader.readAsText(file)
  }

  parseCSV(csvContent) {
    const answers = csvContent.trim()
      .split('\n')
      .map(line => line.trim())
      .filter(line => line)
      .map(line => {
        let parsedText = line
        const quoteMatch = line.match(/^"([^"]*)"/)
        if (quoteMatch) {
          parsedText = quoteMatch[1]
        } else {
          const commaIndex = line.indexOf(',')
          if (commaIndex !== -1) {
            parsedText = line.substring(0, commaIndex)
          }
        }

        return {
          id: uuidv4(),
          name: parsedText.trim()
        }
      })
      .filter(answer => answer.name)

    if (answers.length === 0) {
      throw new Error("No valid answers found in CSV file")
    }

    return answers
  }

  displayMessage(message, type) {
    const target = this[`${type}MessageTarget`]
    const timeoutKey = `${type}Timeout`

    if (this[timeoutKey]) clearTimeout(this[timeoutKey])

    target.classList.remove("hidden")
    target.textContent = message

    this[timeoutKey] = setTimeout(() => {
      target.classList.add("hidden")
      target.textContent = ""
    }, this.constructor.MESSAGE_TIMEOUT)
  }

  displaySuccess(message) {
    this.displayMessage(message, this.constructor.MESSAGE_TYPES.SUCCESS)
  }

  displayError(message) {
    this.displayMessage(message, this.constructor.MESSAGE_TYPES.ERROR)
  }

  clearMessages() {
    Object.values(this.constructor.MESSAGE_TYPES).forEach(type => {
      const timeoutKey = `${type}Timeout`
      const target = this[`${type}MessageTarget`]

      if (this[timeoutKey]) clearTimeout(this[timeoutKey])
      target.classList.add("hidden")
      target.textContent = ""
    })
  }

  dispatchAnswersUploadedEvent({ importedCount, answers }) {
    this.element.dispatchEvent(new CustomEvent("optional-questions:answers-uploaded", {
      bubbles: true,
      detail: {
        importedCount,
        answers,
        questionId: this.questionIdValue
      }
    }))
  }
}
